import { HttpParams } from "@angular/common/http";
import { TemplateRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

export interface GridColumnConfig {
  key: string;
  label: string;
  type: string;
  sort?: boolean;
  filter?: boolean;
  width?: string;
  template?: TemplateRef<any>;
}

export interface GridConfig<T> {
  entityName: string;
  storageKeys: {
    filters: string;
    sort: string;
    page?: string;
  };
  filterFields: {
    [key: string]: {
      backendField: string;
      persistent?: boolean;
      track?: boolean;
      propertyPath?: string;
    };
  };
  apiService: {
    getData?: (filterObj: BackendFilteringObject) => Promise<any>;
    exportData?: (filterObj?: BackendFilteringObject) => Promise<any>;
    exportPageData?: (data: any) => Promise<any>;
  };
  routerDependencies?: {
    router: Router;
    activatedRoute: ActivatedRoute;
  };
  initialFilters?: { [key: string]: string };
  autoLoad?: boolean;
}

export interface SortingItem {
    field: string;
    direction: string;
}
export interface FilteringItem {
    field: string;
    search: string;
}
export interface PaginatorItem {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    from?: number;
    to?: number;
    total?: number;
}
export class BackendFilteringObject {
    private perPage: number = 10;
    private filteringItems: FilteringItem[] = [];
    private sortingItems: SortingItem[] = [];
    private paginatorItem: PaginatorItem = { current_page: 1, per_page: 10 };

    constructor(perPage: number = 10) {
        this.perPage = perPage;
        this.paginatorItem.per_page = perPage;
    }

    setFilteringItems(items: FilteringItem[]): void {
        this.filteringItems = items || [];
    }

    setSortingItems(items: SortingItem[]): void {
        this.sortingItems = items || [];
    }

    get paginator(): PaginatorItem {
        return this.paginatorItem;
    }

    set perPageItems(items: number) {
        this.perPage = items;
        this.paginatorItem.per_page = items;
    }

    get perPageItems(): number {
        return this.perPage;
    }

    setPager(currentPage: number, pageCount: number, perPage?: number, totalItems?: number): void {
        this.paginatorItem.current_page = currentPage;
        this.paginatorItem.last_page = pageCount;
        if (perPage) {
            this.paginatorItem.per_page = perPage;
            this.perPage = perPage;
        }
        if (totalItems) {
            this.paginatorItem.total = totalItems;
        }
    }

    setBackendPager(pager: PaginatorItem): void {
        this.paginatorItem = { ...this.paginatorItem, ...pager };
    }

    set actualPage(page: number) {
        this.paginatorItem.current_page = page;
    }

    get actualPage(): number {
        return this.paginatorItem.current_page || 1;
    }

    set lastPage(pageCount: number) {
        this.paginatorItem.last_page = pageCount;
    }

    get lastPage(): number {
        return this.paginatorItem.last_page || 1;
    }

    getFilter(): any[] {
        return this.filteringItems.map(item => ({
            field: item.field,
            search: item.search
        }));
    }

    getSort(): any[] {
        return this.sortingItems.map(item => ({
            field: item.field,
            direction: item.direction
        }));
    }

    setFilter(filtering: FilteringItem[], sorting: SortingItem[]): void {
        this.setFilteringItems(filtering);
        this.setSortingItems(sorting);
    }

    getBackendFiltering(): string {
        const params = this.getHttpParams();
        return params.toString();
    }

    getHttpParams(): HttpParams {
      // Start with a fresh HttpParams object
      let params = new HttpParams();
      
      // Only set these parameters once
      params = params.set('page', String(this.paginatorItem.current_page || 1));
      params = params.set('limit', String(this.paginatorItem.per_page || 10));
      
      // Add filter parameters, checking if they already exist
      this.filteringItems.forEach(filter => {
          if (filter.field && filter.search !== undefined && filter.search !== null && filter.search !== '') {
              // Convert filter.search to string
              const searchValue = typeof filter.search === 'object' ? 
                  JSON.stringify(filter.search) : 
                  String(filter.search);
              
              // Only set if not already present
              if (!params.has(filter.field)) {
                  params = params.set(filter.field, searchValue);
              }
          }
      });
      
      // Add sorting, checking if not already present
      if (this.sortingItems.length > 0 && !params.has('sort')) {
          const sort = this.sortingItems[0];
          params = params.set('sort', `${sort.field}.${sort.direction}`);
      }
      
      return params;
    }

    getBackendFilteringObject(): any {
        const result: any = {
            paginate: true,
            page: this.paginatorItem.current_page || 1,
            limit: this.paginatorItem.per_page || 10
        };

        this.filteringItems.forEach(filter => {
            if (filter.field && filter.search !== undefined && filter.search !== null && filter.search !== '') {
                const searchValue = typeof filter.search === 'object' ? 
                    JSON.stringify(filter.search) : 
                    String(filter.search);
                
                result[filter.field] = searchValue;
            }
        });

        if (this.sortingItems.length > 0) {
            const sort = this.sortingItems[0];
            result.sort = `${sort.field}.${sort.direction}`;
        }

        return result;
    }

    resetBackendFiltering(): void {
        this.filteringItems = [];
        this.sortingItems = [];
        this.paginatorItem = { current_page: 1, per_page: this.perPage };
    }

    get filtering(): FilteringItem[] {
        return this.filteringItems;
    }

    get sorting(): SortingItem[] {
        return this.sortingItems;
    }
}
